import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUp,
  Github,
  Linkedin,
  Mail,
  MapPin,
  Twitter,
} from "lucide-react";
import { Navbar } from "../public/Navbar";
import Contact from "../sections/Contact";
import { usePortfolio } from "../../contexts/PortfolioContext";

const FOOTER_LINKS = [
  { label: "Work", href: "/#projects" },
  { label: "Experience", href: "/#experience" },
  { label: "Skills", href: "/#skills" },
  { label: "Education", href: "/#education" },
  { label: "Writing", href: "/#articles" },
];

const PublicLayout = ({ children, isDownloading, handleDownload }) => {
  const { pathname } = useLocation();
  const { portfolioData } = usePortfolio();
  const personalInfo = portfolioData.personalInfo || {};
  const year = new Date().getFullYear();
  const name = personalInfo.name || "Aurio Rajaa";
  const isDetailPage =
    pathname.startsWith("/article/") || pathname.startsWith("/project/");

  const socials = [
    personalInfo.github && {
      href: personalInfo.github,
      icon: <Github size={16} />,
      label: "GitHub",
      external: true,
    },
    personalInfo.linkedin && {
      href: personalInfo.linkedin,
      icon: <Linkedin size={16} />,
      label: "LinkedIn",
      external: true,
    },
    personalInfo.twitter && {
      href: personalInfo.twitter,
      icon: <Twitter size={16} />,
      label: "Twitter",
      external: true,
    },
    personalInfo.email && {
      href: `mailto:${personalInfo.email}`,
      icon: <Mail size={16} />,
      label: "Email",
      external: false,
    },
  ].filter(Boolean);

  return (
    <div className="public-shell">
      <Navbar
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      />

      <main className="public-main" id="main-content">
        {children}
      </main>

      <footer className="public-footer" id="contact">
        <div className="container">
          <div
            className={`footer-top${isDetailPage ? " footer-top--compact" : ""}`}
          >
            {!isDetailPage && (
              <div className="footer-contact">
                <p className="public-eyebrow">Get in touch</p>
                <h2 className="footer-contact-title">
                  Let&apos;s build <span>something useful.</span>
                </h2>
                <p className="footer-contact-lead">
                  I&apos;m open to software engineering opportunities, product
                  collaborations, and conversations about backend systems and
                  cloud workflows.
                </p>
                <Contact />
              </div>
            )}

            <div className="footer-nav">
              <div>
                <h3>Explore</h3>
                <ul className="footer-links">
                  {FOOTER_LINKS.map((item) => (
                    <li key={item.href}>
                      <Link to={item.href}>{item.label}</Link>
                    </li>
                  ))}
                </ul>

                <div className="footer-contact-details">
                  {personalInfo.location && (
                    <span>
                      <MapPin size={15} />
                      {personalInfo.location}
                    </span>
                  )}
                  {personalInfo.email && (
                    <a href={`mailto:${personalInfo.email}`}>
                      <Mail size={15} />
                      {personalInfo.email}
                    </a>
                  )}
                </div>
              </div>

              <div>
                <h3>Elsewhere</h3>
                <ul className="footer-links">
                  {socials.map((social) => (
                    <li key={social.label}>
                      <a
                        href={social.href}
                        {...(social.external
                          ? { target: "_blank", rel: "noreferrer noopener" }
                          : {})}
                      >
                        {social.icon}
                        {social.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © {year} {name}. Built in{" "}
              {personalInfo.location || "Jakarta, Indonesia"}.
            </p>
            <button
              type="button"
              className="back-to-top"
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
            >
              Back to top
              <ArrowUp size={14} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
export { FOOTER_LINKS };
